import React, { useState, useMemo, useEffect } from 'react'
import { backendService, LibraryData, Project } from '../services/backendService'

type DeleteTarget = 'girasoles_view' | 'stl_in' | 'stl_out' | 'project' | 'patient'

interface DeleteModalProps {
  show: boolean
  onClose: () => void
  backendLibraryData: LibraryData | null
  doctorId: string | null
  selectedLibraryItem: string | null
  selectedInsoleProject: string | null
  girasolesViewExists: Record<string, boolean>
  onViewDeleted: (projectId: string, viewKey: string) => void
  onRefresh: () => Promise<void>
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  show,
  onClose,
  backendLibraryData,
  doctorId,
  selectedLibraryItem,
  selectedInsoleProject,
  girasolesViewExists,
  onViewDeleted,
  onRefresh,
}) => {
  const [patientId, setPatientId] = useState<string>('')
  const [projectId, setProjectId] = useState<string>('')
  const [target, setTarget] = useState<DeleteTarget | ''>('')

  const patients = useMemo(() => {
    if (!backendLibraryData) return [] as { id: string; name: string; projects: any[] }[]
    const doc = backendLibraryData.doctors.find(d => d.id === doctorId) || backendLibraryData.doctors[0]
    return (doc?.patients || []) as any[]
  }, [backendLibraryData, doctorId])

  const patientProjects = useMemo(() => {
    const p = patients.find(p => p.id === patientId)
    return (p?.projects || []) as Project[]
  }, [patients, patientId])

  const deleteTargets = useMemo(() => {
    const targets: { id: DeleteTarget; label: string }[] = []
    if (!projectId) return targets

    if (girasolesViewExists[projectId]) {
      targets.push({ id: 'girasoles_view', label: 'Girasoles_view' })
    }

    const selectedProject = patientProjects.find(p => p.id === projectId)
    if (selectedProject?.status === 'stl_imported' || selectedProject?.status === 'processed') {
      targets.push({ id: 'stl_in', label: 'STL_IN' })
    }
    if (selectedProject?.status === 'processed') {
      targets.push({ id: 'stl_out', label: 'STL_OUT' })
    }

    // Always allow deleting the project itself
    targets.push({ id: 'project', label: 'Entire Project' })

    return targets
  }, [girasolesViewExists, patientProjects, projectId])

  // Show patient-level deletion when no project selected
  const patientLevelTargets: { id: DeleteTarget; label: string }[] = useMemo(
    () => patientId ? [{ id: 'patient', label: 'Entire Patient (all projects)' }] : [],
    [patientId],
  )

  useEffect(() => {
    if (!show) return
    setPatientId(selectedLibraryItem ?? '')
    setProjectId(selectedInsoleProject ?? '')
    setTarget('')
  }, [selectedInsoleProject, selectedLibraryItem, show])

  useEffect(() => {
    const allTargets = [...deleteTargets, ...patientLevelTargets]
    if (allTargets.some(o => o.id === target)) return
    setTarget(allTargets[0]?.id ?? '')
  }, [deleteTargets, patientLevelTargets, target])

  if (!show) return null

  const confirmAndDelete = async () => {
    if (!patientId) { alert('Please select a patient'); return }

    if (target === 'patient') {
      if (!window.confirm('Delete this patient and ALL their projects? This cannot be undone.')) return
      try {
        await backendService.deletePatient(patientId)
        await onRefresh()
        alert('Patient deleted.')
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Deletion failed'
        alert(`Failed to delete patient: ${msg}`)
      }
      onClose()
      return
    }

    if (!projectId) { alert('Please select an insole'); return }
    if (!target) { alert('Please select what to delete'); return }

    let confirmMsg = ''
    switch (target) {
      case 'girasoles_view': confirmMsg = 'Delete Girasoles_view for the selected insole?'; break
      case 'stl_in': confirmMsg = 'Delete STL_IN for the selected insole?'; break
      case 'stl_out': confirmMsg = 'Delete STL_OUT for the selected insole?'; break
      case 'project': confirmMsg = 'Delete the entire project and all its files? This cannot be undone.'; break
    }

    if (!window.confirm(confirmMsg)) return

    try {
      if (target === 'girasoles_view') {
        onViewDeleted(projectId, 'girasoles-view')
        alert('Girasoles_view deleted (UI state cleared).')
      } else if (target === 'project') {
        await backendService.deleteProject(projectId)
        await onRefresh()
        alert('Project deleted.')
      } else {
        const role = target === 'stl_in' ? 'stl_in' : 'stl_out'
        await backendService.deleteSTLFile(projectId, role)
        await onRefresh()
        alert(`${target === 'stl_in' ? 'STL_IN' : 'STL_OUT'} deleted.`)
      }
    } catch (e) {
      console.error('Delete failed', e)
      alert('Deletion failed. Backend may not implement this endpoint yet.')
    } finally { onClose() }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Delete</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Patient</label>
            <select value={patientId} onChange={e => { setPatientId(e.target.value); setProjectId(''); setTarget('') }}>
              <option value="">Select a patient...</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {patientId && (
            <div className="form-group">
              <label>Insole (optional — leave empty to delete entire patient)</label>
              <select value={projectId} onChange={e => { setProjectId(e.target.value); setTarget('') }}>
                <option value="">-- Delete entire patient --</option>
                {patientProjects.map(pr => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
              </select>
            </div>
          )}

          {projectId && (
            <div className="form-group">
              <label>Delete target</label>
              <select value={target} onChange={e => setTarget(e.target.value as DeleteTarget)} disabled={deleteTargets.length === 0}>
                <option value="">{deleteTargets.length === 0 ? 'No deletable items' : 'Select...'}</option>
                {deleteTargets.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          )}

          {patientId && !projectId && (
            <div className="form-group">
              <label>Delete target</label>
              <select value={target} onChange={e => setTarget(e.target.value as DeleteTarget)}>
                <option value="">Select...</option>
                {patientLevelTargets.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-danger"
            onClick={confirmAndDelete}
            disabled={!patientId || (!!projectId && !target)}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeleteModal
